import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../Components/Admin/AdminLayout';
import { Firebase } from '../../firebase/config'; // Ensure direct Firebase reference for Storage access
import { getProductRef, productsRef, serverTimestamp } from '../../firebase/collections';

function AdminAds() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  // 🛠️ EXTENDED EDIT CONTROL STATES
  const [editingProduct, setEditingProduct] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editImages, setEditImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false); // Spinner for file processing
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    let q = productsRef().orderBy('createdAt', 'desc').limit(100);

    if (statusFilter) {
      q = productsRef()
        .where('status', '==', statusFilter)
        .orderBy('createdAt', 'desc')
        .limit(100);
    }

    q.get()
      .then((snap) => {
        setAds(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const updateStatus = (id, newStatus) => {
    getProductRef(id)
      .update({ status: newStatus, updatedAt: serverTimestamp() })
      .then(() => {
        setAds((prev) => prev.filter((a) => a.id !== id));
      });
  };

  const handleOpenEdit = (product) => {
    setEditingProduct(product);
    setEditTitle(product.name || product.productName || '');
    setEditPrice(product.price || '');
    setEditDescription(product.description || '');
    setEditImages(product.images || []);
  };

  // 🔥 NATIVE FILE UPLOAD TO FIREBASE STORAGE RUNTIME MULTIPART ENGINE
  const handleImageUploadClick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);

    // Construct an isolated unique storage path template node
    const storagePath = `products/${editingProduct.id || 'admin_uploads'}_${Date.now()}_${file.name}`;
    const storageRef = Firebase.storage().ref().child(storagePath);

    try {
      // 1. Upload binary stream asset blob
      const snapshot = await storageRef.put(file);

      // 2. Extract public secure CDN download URL link target
      const secureDownloadUrl = await snapshot.ref.getDownloadURL();

      // 3. Append securely directly onto active local mapping arrays loop logic
      setEditImages((prev) => [...prev, secureDownloadUrl]);
      alert("Image binary successfully uploaded and mounted to array!");
    } catch (err) {
      console.error("Cloud Storage transaction fault:", err);
      alert(`Upload operation dropped: \n${err.message}`);
    } finally {
      setUploadingImage(false);
      e.target.value = null; // Clear standard input selection targets
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setEditImages((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleUpdateAdDetails = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await getProductRef(editingProduct.id).update({
        name: editTitle,
        productName: editTitle,
        price: Number(editPrice),
        description: editDescription,
        images: editImages,
        url: editImages.length > 0 ? editImages[0] : '',
        updatedAt: serverTimestamp()
      });

      setAds((prev) =>
        prev.map((a) =>
          a.id === editingProduct.id
            ? {
                ...a,
                name: editTitle,
                productName: editTitle,
                price: Number(editPrice),
                description: editDescription,
                images: editImages,
                url: editImages.length > 0 ? editImages[0] : ''
              }
            : a
        )
      );

      setEditingProduct(null);
      alert("Ad configuration saved successfully!");
    } catch (err) {
      console.error("Error updating listing metrics:", err);
      alert(`Could not save modifications: \n${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="sec-hdr">
        <h1 className="sec-title">Marketplace Moderation</h1>
        <p className="sec-sub">Review pending ads to make them live.</p>
      </div>

      <div className="search-strip" style={{ margin: '20px 0' }}>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="pending">⏳ Pending Review</option>
          <option value="active">✅ Live Ads</option>
          <option value="rejected">❌ Rejected</option>
        </select>
      </div>

      {loading ? (<div>Loading...</div>) : (
        <table className="adminTable">
          <thead>
            <tr>
              <th>EQUIPMENT</th>
              <th>PRICE</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {ads.map((a) => (
              <tr key={a.id}>
                <td>{a.name}</td>
                <td>₹{a.price}</td>
                <td>{a.status || 'pending'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Link className="btn btn-outline" to={`/ad/${a.id}`} target="_blank">Preview</Link>

                    <button
                      className="btn btn-outline"
                      onClick={() => handleOpenEdit(a)}
                      style={{ borderColor: '#2a9d8f', color: '#2a9d8f' }}
                    >
                      Edit
                    </button>

                    {a.status !== 'active' && (
                      <button className="btn btn-green" onClick={() => updateStatus(a.id, 'active')}>Approve</button>
                    )}

                    {a.status !== 'rejected' && (
                      <button className="btn btn-outline" onClick={() => updateStatus(a.id, 'rejected')}>Reject</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* --- 📦 ADVANCED EDIT MODAL OVERLAY WITH UPLOAD FUNCTIONALITY --- */}
      {editingProduct && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 2000,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: '#fff', padding: '24px', borderRadius: '12px',
            width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontWeight: '700', fontSize: '18px', color: '#1e293b' }}>
              Deep Edit Product Specifications
            </h3>

            <form onSubmit={handleUpdateAdDetails}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>EQUIPMENT NAME</label>
                <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box' }} />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>PRICE (₹)</label>
                <input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} required style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box' }} />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>ITEM DESCRIPTION</label>
                <textarea rows="3" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} required style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>

              {/* 🖼️ LOCAL UPLOAD AND GALLERY PANEL GRID */}
              <div style={{ marginBottom: '20px', border: '1px solid #e2e8f0', padding: '12px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '8px' }}>
                  Manage Image Cluster ({editImages.length})
                </label>

                {/* Inline Thumbnail Matrix Lookups */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                  {editImages.map((imgUrl, index) => (
                    <div key={index} style={{ position: 'relative', width: '70px', height: '70px', border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: '#fff', overflow: 'hidden' }}>
                      <img src={imgUrl} alt={`preview-${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(239, 68, 68, 0.85)', color: '#fff', border: 'none', borderRadius: '50%', width: '18px', height: '18px', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>

                {/* 🔥 HARDWARE ATTACHMENT ACTION BUTTON INTERFACE */}
                <div style={{ backgroundColor: '#ffffff', border: '2px dashed #cbd5e1', borderRadius: '8px', padding: '14px', textAlign: 'center', position: 'relative' }}>
                  {uploadingImage ? (
                    <span style={{ fontSize: '13px', color: '#2a9d8f', fontWeight: '600' }}>📤 Synchronizing Image onto Cloud Storage...</span>
                  ) : (
                    <>
                      <span style={{ fontSize: '13px', color: '#475569', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                        ➕ Upload Local Image File
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUploadClick}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>Click anywhere within this area to select</span>
                    </>
                  )}
                </div>
              </div>

              {/* Actions Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setEditingProduct(null)}
                  style={{ padding: '8px 16px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', color: '#475569' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || uploadingImage}
                  style={{ padding: '8px 20px', background: '#2a9d8f', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminAds;
